import * as React from "react";
import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Checkbox, Divider, Grid } from "@mui/material";
import type { IEnvironment } from "../../../types/EnvironmentTypes";
import { useEnvironmentService } from "../../../services/registry/EnvironmentService";
import { EnvironmentSelector } from "../../../components/EnvironmentSelector";
import { FeatureValue, AP_HIDDEN_FEATURES, AP_DEFAULT_TICKED_CHECKBOXES } from "utils/Features";

export interface ReplicationItem {
  key: string;
  sourceValue: FeatureValue;
  destValue: FeatureValue;
  replicate: boolean;
  label?: string;
}

export interface EnvironmentReplicationPopupProps {
  visible: boolean;
  initialSourceEnvironment?: IEnvironment;
  destEnvironment: IEnvironment;
  onDismiss: () => void;
  onReplicate: (sourceEnvironment: IEnvironment, destEnvironment: IEnvironment, itemsToReplicate: ReplicationItem[]) => void;
}

export const EnvironmentReplicationPopup = ({
  visible,
  initialSourceEnvironment,
  destEnvironment,
  onDismiss,
  onReplicate,
}: EnvironmentReplicationPopupProps) => {
  const environmentService = useEnvironmentService();
  const [loading, setLoading] = useState(true);
  const [replicationItems, setReplicationItems] = useState<ReplicationItem[]>([]);
  const [sourceEnvironment, setSourceEnvironment] = useState<IEnvironment | undefined>(initialSourceEnvironment);
  const [sourceEnvChanged, setSourceEnvChanged] = useState(true);
  const [sourceFeatures, setSourceFeatures] = useState<Record<string, FeatureValue>>({});
  const [destFeatures, setDestFeatures] = useState<Record<string, FeatureValue>>({});

  useEffect(() => {
    setSourceEnvironment(initialSourceEnvironment);
  }, [initialSourceEnvironment]);

  useEffect(() => {
    // sourceEnvChanged ensures that loadFeatures is only called once after source environment is picked
    // avoiding the selected items to change on every re-render
    if (sourceEnvChanged && visible && sourceEnvironment && destEnvironment) {
      loadFeatures();
      setSourceEnvChanged(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, sourceEnvironment, destEnvironment]);

  const handleSourceEnvironmentSelected = (env: IEnvironment | undefined) => {
    setSourceEnvironment(env);
    setSourceEnvChanged(true);
    setReplicationItems([]);
  };

  useEffect(() => {
    if (!sourceEnvironment) return;

    const featureKeys = new Set<string>([...Object.keys(sourceFeatures || {}), ...Object.keys(destFeatures || {})]);

    const hiddenFeatures = (sourceFeatures[AP_HIDDEN_FEATURES] || []) as string[];
    const defaultTickedCheckboxes = (sourceFeatures[AP_DEFAULT_TICKED_CHECKBOXES] || []) as string[];

    const items: ReplicationItem[] = [
      {
        key: "backendImageId",
        sourceValue: sourceEnvironment.backendImageId,
        destValue: destEnvironment.backendImageId,
        replicate: true,
        label: "Backend Image ID",
      },
    ];

    featureKeys.forEach((key) => {
      if (hiddenFeatures.includes(key)) return;
      items.push({
        key,
        sourceValue: sourceFeatures[key],
        destValue: destFeatures[key],
        replicate: defaultTickedCheckboxes.includes(key),
      });
    });

    setReplicationItems(items);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFeatures, destFeatures]);

  const loadFeatures = async () => {
    if (!sourceEnvironment) return;

    setLoading(true);

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let sourceResponse: any = { features: {} };
      if (sourceEnvironment) {
        sourceResponse = await environmentService.getAllFeatures(sourceEnvironment.name);
      }
      const destResponse = await environmentService.getAllFeatures(destEnvironment.name);

      setSourceFeatures(sourceResponse.features);
      setDestFeatures(destResponse.features);
    } catch (error) {
      console.error("Failed to load features:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleReplication = (key: string) => {
    setReplicationItems(replicationItems.map((item) => (item.key === key ? { ...item, replicate: !item.replicate } : item)));
  };

  const handleReplicate = async () => {
    if (!sourceEnvironment) return;

    onReplicate(
      sourceEnvironment,
      destEnvironment,
      replicationItems.filter((item) => item.replicate),
    );
  };

  const formatValue = (value: FeatureValue): string => {
    if (value === undefined || value === null) {
      return "NOT SET";
    }
    if (typeof value === "boolean") {
      return value ? "true" : "false";
    }
    if (typeof value === "object") {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return "{}";
      }
    }
    return String(value);
  };

  const renderValueWithStyle = (value: FeatureValue) => {
    const formattedValue = formatValue(value);
    return (
      <Typography
        variant="body2"
        sx={{
          fontWeight: formattedValue === "NOT SET" ? "normal" : "bold",
          color: formattedValue === "NOT SET" ? "text.secondary" : "text.primary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {formattedValue}
      </Typography>
    );
  };

  const renderComparisonView = () => {
    return (
      <>
        <Grid container spacing={2} sx={{ alignItems: "center", mb: 2, px: 2 }}>
          <Grid size={5.5}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                {"Source =>"}
              </Typography>
              <div style={{ flexGrow: 1 }} />
              <EnvironmentSelector
                id="replication-source"
                selectedEnvironment={sourceEnvironment}
                onEnvironmentSelected={handleSourceEnvironmentSelected}
                useStorage={false}
              />
            </div>
          </Grid>
          <Grid size={1} />
          <Grid size={5.5}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Typography variant="h6">{destEnvironment.name}</Typography>
              <div style={{ flexGrow: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                {"<= Destination"}
              </Typography>
            </div>
          </Grid>
          <Grid size={12}>
            <Divider />
          </Grid>
        </Grid>

        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {replicationItems.map((item, index) => {
            const valuesDiffer = formatValue(item.destValue) !== formatValue(item.sourceValue);
            return (
              <Grid
                container
                key={item.key}
                sx={{
                  alignItems: "center",
                  px: 2,
                  backgroundColor: valuesDiffer ? "warning.light" : "transparent",
                  borderRadius: valuesDiffer ? 1 : 0,
                }}
              >
                <Grid size={5.5}>
                  <div style={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1, flexShrink: 0 }}>
                      {`${item.label || item.key} =>`}
                    </Typography>
                    <div style={{ flexGrow: 1 }} />
                    {renderValueWithStyle(item.sourceValue)}
                  </div>
                </Grid>

                <Grid size={1} sx={{ display: "flex", justifyContent: "center" }}>
                  <Checkbox checked={item.replicate} onChange={() => handleToggleReplication(item.key)} color="primary" />
                </Grid>

                <Grid size={5.5}>
                  <div style={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
                    {renderValueWithStyle(item.replicate ? item.sourceValue : item.destValue)}
                    <div style={{ flexGrow: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1, flexShrink: 0 }}>
                      {`<= ${item.label || item.key}`}
                    </Typography>
                  </div>
                </Grid>

                {index < replicationItems.length - 1 && (
                  <Grid size={12}>
                    <Divider />
                  </Grid>
                )}
              </Grid>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <Dialog open={visible} onClose={onDismiss} maxWidth="lg" fullWidth>
      <DialogTitle>Replicate Environment Configuration</DialogTitle>
      <DialogContent>{renderComparisonView()}</DialogContent>
      <DialogActions>
        <Button onClick={onDismiss}>Cancel</Button>
        <Button
          onClick={handleReplicate}
          color="primary"
          variant="contained"
          disabled={loading || replicationItems.length === 0}
        >
          Replicate
        </Button>
      </DialogActions>
    </Dialog>
  );
};
